import {
    DecisionDataStore,
    UnderReview,
    Approved,
    Declined,
    PrequalificationDecision,
} from '@servicetitan/financing-decision-shared';
import {
    SetupDataStore,
    useProviderConfig,
    WorkflowStage,
    WorkflowStore,
} from '@servicetitan/financing-workflow-shared';
import { SubmitApplicationDecision } from '@servicetitan/financing-api-client';
import { PrequalificationStatus } from '@servicetitan/financing-api-client/src/prequals-api';
import {
    ApplicationSubmitToDecisionProvider,
    ErrorBoundary,
    PlansEntryReason,
    PlansListTelemetryStore,
    reportActiveApplicationSubmitToDecisionOutcome,
} from '@servicetitan/financing-utils-shared';
import {
    WorkflowLayoutsStore,
    ExternalApplicationWorkflowStore,
    SubmitLoan,
    DecisionWorkflow,
    SecondLookDecisionWorkflow,
    ResetWorkflow,
    CustomerAuthStep,
    PrequalsSelectPlanStep,
    ReviewingWorkflow,
    WellsFargoReviewingWorkflow,
    ApplicationWithCoApplicationWorkflow,
    TransitionToHardApp,
    SUBMIT_TO_DECISION_STAGES,
} from '@servicetitan/financing-application-workflow-shared';
import { PrequalificationWorkflow } from './PrequalificationWorkflow';
import { PrequalificationReviewingWorkflow } from './PrequalificationReviewingWorkflow';
import { ReactNode } from 'react';

interface WorkflowRendererProps {
    workflowStage: WorkflowStage;
    canChangeProvider?: boolean;
}

const reportSubmitToDecisionError = () => {
    reportActiveApplicationSubmitToDecisionOutcome('error');
};

export const WorkflowRenderer = ({ workflowStage }: WorkflowRendererProps) => {
    const { decisionData } = DecisionDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { prequalDecisionData } =
        PrequalificationDecision.PrequalificationDecisionDataStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { DefaultLayout } = WorkflowLayoutsStore.useStore();
    const { ExternalApplicationWorkflow } = ExternalApplicationWorkflowStore.useStore();
    const { setNextEntryReason } = PlansListTelemetryStore.useStore();
    const { useCustomerAuthFlow, hasMailingAddressStep } = useProviderConfig();
    const hasTransitionedToHardApp = setupData?.hasTransitionedToHardApp;
    const isApplyingForPrequalification = setupData?.isApplyingForPrequalification;

    // Check if this is a GoodLeap prequalification flow
    const isGoodLeapPrequalification =
        isApplyingForPrequalification && setupData?.provider?.toLowerCase() === 'goodleap';

    const getApplicationWorkflowStage = () => {
        // Providers without separate mailing address step use the compact workflow
        if (Boolean(isGoodLeapPrequalification) || !hasMailingAddressStep) {
            return 'application-with-co-application';
        }
        return 'application';
    };

    const onPlanSelected = () => {
        if (useCustomerAuthFlow && !hasTransitionedToHardApp) {
            setWorkflowStage('customer-auth');
        } else if (setupData?.provider?.toLowerCase() === 'greensky' && !hasTransitionedToHardApp) {
            // PrequalificationWorkflow has no Job Details step, so it can only supply an address
            // through mailingAddress. Once the applicant transitions, useProviderConfig switches
            // GreenSky onto the embedded hard-app config, which resolves the address from
            // jobDetails — so the hard app has to use the compact workflow that collects it.
            setWorkflowStage('application');
        } else {
            setWorkflowStage(getApplicationWorkflowStage());
        }
    };

    let stageContent: ReactNode;

    switch (workflowStage) {
        case 'external-application':
            stageContent = <ExternalApplicationWorkflow />;
            break;
        // Writing prequalificationId during the soft→hard handoff swaps the technician app onto
        // this renderer, so it has to handle the stage too or the applicant lands on a blank step.
        case 'transition-to-hard-app':
            stageContent = (
                <TransitionToHardApp
                    onCompleted={() => {
                        // Without staging the reason, the plans remount is recorded as the first
                        // plans attempt and inflates the as-low-as funnel.
                        setNextEntryReason(PlansEntryReason.SoftToHardTransition);
                        setWorkflowStage('plan-selection');
                    }}
                />
            );
            break;
        case 'customer-auth':
            stageContent = (
                <DefaultLayout>
                    <CustomerAuthStep onCompleted={() => setWorkflowStage('application')} />
                </DefaultLayout>
            );
            break;
        case 'plan-selection':
            stageContent = (
                <DefaultLayout>
                    <PrequalsSelectPlanStep onPlanSelected={onPlanSelected} />
                </DefaultLayout>
            );
            break;
        case 'application':
            stageContent = <PrequalificationWorkflow />;
            break;
        case 'application-with-co-application':
            stageContent = <ApplicationWithCoApplicationWorkflow />;
            break;
        case 'submitting':
            stageContent = (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError="submitting a loan"
                        onErrorTracked={reportSubmitToDecisionError}
                        onRetry={() => setWorkflowStage(getApplicationWorkflowStage())}
                    >
                        <SubmitLoan />
                    </ErrorBoundary>
                </DefaultLayout>
            );
            break;
        case 'decision':
            stageContent =
                prequalDecisionData?.status === PrequalificationStatus.Approved ? (
                    <DefaultLayout>
                        <PrequalificationDecision.Approved />
                    </DefaultLayout>
                ) : prequalDecisionData?.status === PrequalificationStatus.Declined ? (
                    <DefaultLayout>
                        <PrequalificationDecision.Declined />
                    </DefaultLayout>
                ) : prequalDecisionData?.status === PrequalificationStatus.Review ||
                  decisionData.decision === SubmitApplicationDecision.Review ? (
                    <DefaultLayout>
                        <UnderReview />
                    </DefaultLayout>
                ) : decisionData.decision === SubmitApplicationDecision.Approved ? (
                    <DefaultLayout>
                        <Approved />
                    </DefaultLayout>
                ) : decisionData.decision === SubmitApplicationDecision.Declined ? (
                    <DefaultLayout>
                        <Declined />
                    </DefaultLayout>
                ) : (
                    // Fallback to standard decision workflow for other cases
                    <DecisionWorkflow />
                );
            break;
        case 'second-look-decision':
            stageContent = (
                <DefaultLayout>
                    <ErrorBoundary actionCausingError="loading qualification decision">
                        <SecondLookDecisionWorkflow />
                    </ErrorBoundary>
                </DefaultLayout>
            );
            break;
        case 'reviewing': {
            const isWellsFargoProvider = setupData?.provider?.toLowerCase() === 'wellsfargo';
            stageContent = (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError={
                            isWellsFargoProvider || isApplyingForPrequalification
                                ? 'reviewing your pre-qualification'
                                : 'reviewing your application'
                        }
                        onErrorTracked={reportSubmitToDecisionError}
                        onRetry={() => setWorkflowStage(getApplicationWorkflowStage())}
                    >
                        {isWellsFargoProvider ? (
                            <WellsFargoReviewingWorkflow />
                        ) : isApplyingForPrequalification ? (
                            <PrequalificationReviewingWorkflow />
                        ) : (
                            <ReviewingWorkflow />
                        )}
                    </ErrorBoundary>
                </DefaultLayout>
            );
            break;
        }
        case 'reset':
            stageContent = <ResetWorkflow />;
            break;
        default:
            stageContent = null;
    }

    if (SUBMIT_TO_DECISION_STAGES.includes(workflowStage)) {
        return (
            <ApplicationSubmitToDecisionProvider
                context={{
                    provider: setupData?.provider,
                    experience: setupData?.role,
                    decision: decisionData?.decision,
                }}
            >
                {stageContent}
            </ApplicationSubmitToDecisionProvider>
        );
    }

    return stageContent;
};
